import { useMemo } from "react";
import { VelavuSite } from "velavu-js-api";
import { MapHelperInstance } from "../../helper/map-helper";
import { useMappedinFloor } from "../../helper/mappedin-helper";
import useMapboxInjectableMappedinFloorplans from "./mapbox-injectable-mappedin-floorplans";
import useMapboxInjectableSiteFloorplans from "./mapbox-injectable-site-floorplans";

export interface MapboxInjectableSitesProps {
	sites?: VelavuSite[] | undefined;
	floorOverride?: { [key: string]: string | undefined };
	highlightedSiteIDs?: string[];
	belowLayer?: string;
}

export default function useMapboxInjectableSites(
	mapInstance: MapHelperInstance,
	props: MapboxInjectableSitesProps,
): string | undefined {
	//Determine which sites to target for Mappedin floor rendering
	const mappedinTargetSites = useMemo(() => {
		const highlightedSiteIDs = props.highlightedSiteIDs;
		if (
			highlightedSiteIDs === undefined ||
			highlightedSiteIDs.length === 0 ||
			props.sites === undefined
		) {
			return [];
		}

		const sitesByID = new Map(props.sites.map((site) => [site.id, site]));
		return highlightedSiteIDs
			.map((siteID) => sitesByID.get(siteID))
			.filter((site) => site !== undefined);
	}, [props.highlightedSiteIDs, props.sites]);

	//Get the Mappedin floors for the Mappedin target sites
	const mappedinFloorDataArray = useMappedinFloor(
		mappedinTargetSites,
		props.floorOverride,
	);

	//Get the IDs of the Mappedin target sites
	const mappedinFloorSiteIDs = useMemo(
		() =>
			new Set(
				mappedinFloorDataArray.map((floorData) => floorData.siteID),
			),
		[mappedinFloorDataArray],
	);

	//Determine which sites to display as Velavu floorplans
	const velavuDisplaySites = useMemo(() => {
		if (props.sites === undefined || mappedinFloorSiteIDs.size === 0) {
			return props.sites;
		}
		return props.sites.filter((site) => !mappedinFloorSiteIDs.has(site.id));
	}, [props.sites, mappedinFloorSiteIDs]);

	//Render Velavu floorplans
	const velavuFloorplanLayerID = useMapboxInjectableSiteFloorplans(
		mapInstance,
		{
			sites: velavuDisplaySites,
			floorOverride: props.floorOverride,
			highlightedSiteIDs: props.highlightedSiteIDs,
			belowLayer: props.belowLayer,
		},
	);

	//Render Mappedin floorplans
	const mappedinFloorArray = useMemo(() => {
		return mappedinFloorDataArray.map((floorData) => floorData.floor);
	}, [mappedinFloorDataArray]);
	const mappedinFloorplanLayerID = useMapboxInjectableMappedinFloorplans(
		mapInstance,
		{
			floors: mappedinFloorArray,
			belowLayer: props.belowLayer,
		},
	);

	//Return the layer ID of the last rendered floorplan layer
	if (mappedinFloorArray.length !== 0) {
		return mappedinFloorplanLayerID;
	} else {
		return velavuFloorplanLayerID;
	}
}
